/**
 * Decodes the on-disk tile serialisations into a placed-cell list, free of React
 * and THREE. TileMapLayer's `tile_map_data` is a 2-byte little-endian format
 * header, then one 12-byte record per cell: int16 x, int16 y, uint16 source_id,
 * uint16 atlas_x, uint16 atlas_y, uint16 alternative_tile.
 */
import { warn } from '../../../../logger';
import { ARRAY_LITERAL_RE, packedArrayLiteral } from '../../../../godot/variant/variantParser.js';
import { int32Elements } from '../../../../godot/variant/packedInt32.js';
import { decodeBase64Bytes } from '../../../../godot/variant/packedBytes.js';
const PACKED_BYTE_ARRAY_RE = packedArrayLiteral('PackedByteArray');
const PACKED_INT32_ARRAY_RE = packedArrayLiteral('PackedInt32Array');
/** `CryptoCore::b64_decode`'s alphabet, and the only padding it accepts. */
const QUOTED_BASE64_RE = /^"([A-Za-z0-9+/]*={0,2})"$/;
/**
 * Why Godot's text parser would refuse this `tile_map_data` literal. A fault is a
 * whole-file `ERR_PARSE_ERROR` (variant_parser.cpp:618-622), so the validator
 * reports it and the tile-data rule skips the literal rather than report twice.
 * A bare `[…]` of bytes is a literal too: PACKED_BYTE_ARRAY converts from ARRAY
 * (variant.cpp:772-778). Its body is decimal only, since a quoted element would
 * convert through `String::to_int` and is no base64 body.
 */
export function readTileMapDataLiteral(value) {
    const trimmed = value.trim();
    const match = PACKED_BYTE_ARRAY_RE.exec(trimmed);
    if (!match) {
        const bare = ARRAY_LITERAL_RE.exec(trimmed)?.[1].trim();
        return bare === undefined || bare.startsWith('"')
            ? { fault: 'not-a-literal' }
            : { fault: null, body: bare };
    }
    const body = match[1].trim();
    if (body.startsWith('"') && !QUOTED_BASE64_RE.test(body)) {
        return { fault: 'invalid-base64', body };
    }
    return { fault: null, body };
}
const CELL_BYTES = 12;
/** Legacy `layer_N/tile_data` packs one cell per three int32s. */
const INTS_PER_LEGACY_CELL = 3;
const HEADER_BYTES = 2;
const FORMAT_VERSION = 0;
export function decodeTileMapData(value) {
    const literal = readTileMapDataLiteral(value);
    if (literal.fault === 'not-a-literal')
        return null;
    const bytes = decodeBytes(literal.body);
    if (!bytes)
        return null;
    // `set_tile_map_data_from_array` clears the layer on empty data, before it
    // reads a header (tile_map_layer.cpp:3216-3218).
    if (bytes.length === 0)
        return [];
    if (bytes.length < HEADER_BYTES || (bytes.length - HEADER_BYTES) % CELL_BYTES !== 0) {
        warn(`[TileMapLayer] tile_map_data truncated (${bytes.length} bytes) — ignoring tile data`);
        return null;
    }
    const view = new DataView(bytes.buffer);
    const version = view.getUint16(0, true);
    if (version !== FORMAT_VERSION) {
        warn(`[TileMapLayer] unknown tile_map_data format version ${version} — ignoring tile data`);
        return null;
    }
    return decodeCellRecords(view, HEADER_BYTES);
}
/**
 * The elements of a packed int body: null overall for text Godot cannot read,
 * which fails the load, and NaN per element for one no int32 holds (`inf`,
 * `1e20`), which Godot loads as a sentinel, so only its cell is unknowable and
 * {@link dropUnstorableRecords} drops that cell alone.
 */
function readInt32Elements(body, context) {
    const elements = int32Elements(body);
    if (elements === null)
        warn(`${context} has entries Godot cannot read — ignoring tile data`);
    return elements;
}
/**
 * Every fixed-stride record with no unstorable (NaN) element, so an unstorable element
 * costs only its own cell: voiding the stream draws nothing, and a zero draws a
 * cell at the origin. A trailing partial record passes through to the caller's
 * truncation check.
 */
function dropUnstorableRecords(elements, stride, headerLength, context) {
    const out = elements.slice(0, headerLength);
    if (out.some(Number.isNaN)) {
        warn(`${context} header is not a value Godot can hold — ignoring tile data`);
        return null;
    }
    let i = headerLength;
    for (; i + stride <= elements.length; i += stride) {
        const record = elements.slice(i, i + stride);
        if (record.some(Number.isNaN)) {
            warn(`${context} has a cell Godot cannot place — dropping that cell`);
            continue;
        }
        out.push(...record);
    }
    for (; i < elements.length; i++)
        out.push(Number.isNaN(elements[i]) ? 0 : elements[i]);
    return out;
}
/**
 * Decodes legacy TileMap `layer_N/tile_data`. `format` is the TileMapDataFormat
 * enum: 2 (TILE_MAP_DATA_FORMAT_3) packs int32 triplets as the 12-byte record
 * above. Formats 0 and 1 are Godot 3 encodings, out of scope, so they warn.
 */
export function decodeLegacyTileData(value, format) {
    if (format !== 2) {
        warn(`[TileMap] tile data format ${format} is a Godot 3 format — ignoring tile data`);
        return null;
    }
    const m = PACKED_INT32_ARRAY_RE.exec(value);
    if (!m)
        return null;
    const read = readInt32Elements(m[1], '[TileMap] tile_data');
    if (read === null)
        return null;
    const ints = dropUnstorableRecords(read, INTS_PER_LEGACY_CELL, 0, '[TileMap] tile_data');
    if (ints === null)
        return null;
    if (ints.length % INTS_PER_LEGACY_CELL !== 0) {
        warn(`[TileMap] tile_data length ${ints.length} is not a whole number of cells — ignoring tile data`);
        return null;
    }
    return decodeCellRecords(new DataView(new Int32Array(ints).buffer), 0);
}
function decodeCellRecords(view, startOffset) {
    const cells = [];
    for (let offset = startOffset; offset + CELL_BYTES <= view.byteLength; offset += CELL_BYTES) {
        cells.push({
            coords: { x: view.getInt16(offset, true), y: view.getInt16(offset + 2, true) },
            sourceId: view.getUint16(offset + 4, true),
            atlasCoords: { x: view.getUint16(offset + 6, true), y: view.getUint16(offset + 8, true) },
            alternativeId: view.getUint16(offset + 10, true),
        });
    }
    return cells;
}
/** Godot ≤4.2 writes comma-separated ints; 4.3+ writes a base64 string. */
function decodeBytes(body) {
    const base64 = /^"([^"]*)"$/.exec(body);
    if (base64) {
        try {
            return decodeBase64Bytes(base64[1]);
        }
        catch {
            warn(`[TileMapLayer] tile_map_data is not valid base64 — ignoring tile data`);
            return null;
        }
    }
    const read = readInt32Elements(body, '[TileMapLayer] tile_map_data');
    if (read === null)
        return null;
    const ints = dropUnstorableRecords(read, CELL_BYTES, HEADER_BYTES, '[TileMapLayer] tile_map_data');
    return ints === null ? null : new Uint8Array(ints);
}
//# sourceMappingURL=tileData.js.map